import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import {
    Card,
    CardContent,
    CardActions,
    Box,
    Avatar,
    Typography,
    styled,
    Link,
    Checkbox,
} from '@mui/material'
import appConfig from 'config'
import { TextFormatter } from 'components'
import { MIDPathToImage } from 'packages/core/helpers'
import { useTranslation } from 'react-i18next'

import * as DynamicComponents from 'components/DynamicComponents'
import { Icon } from 'packages/eid-icons'
import { isNilOrEmpty } from 'packages/core'
import { Divider } from 'packages/eid-ui'

const colors = {
    primary: '#0057A0',
    grey: '#b4b4b4',
    border: '#f2f2f3',
}

const MyCard = styled(Card)({
    margin: 'auto',
    padding: '0px 10px 5px 10px',
})

const StyledCardActions = styled(CardActions)({
    paddingBottom: '12px',
    paddingTop: '24px',
})

const StyledCardContent = styled(CardContent)({
    padding: '16px 0px',
    borderBottom: '1px solid ' + colors.border,
})

const StyledTypography = styled(Typography)({
    fontSize: '15px',
    textAlign: 'left',
    color: '#0057A0',
})

const StyledFieldBlock = styled(Box)({
    '& div': {
        textAlign: 'left',
    },
    '&:first-of-type': {
        paddingRight: '10px',
        '& div': {
            float: 'right',
        },
    },
    '&:last-child': {
        paddingLeft: '10px',
    },
})

const StyledLink = styled(Link)({
    fontWeight: 'bold',
    color: '#0057A0',
    lineHeight: '1',
    fontSize: '15px',
    textAlign: 'left',
})

const StyledAvatar = styled(Avatar)({
    width: '54px',
    height: '54px',
})

const StyledTable = styled('table')({
    '& th': {
        color: colors.grey,
        fontWeight: 'normal',
        padding: '0px 5px',
    },
    '& td': {
        padding: '0px 5px',
    },
    tableLayout: 'fixed',
    width: '100%',
})

function DelegationCard({
    data,
    handleCardChecked,
    selectAll,
    rowSelection,
    setSelectAll,
    checked,
    hideActions,
    showDelegator,
    attributes,
}) {
    const {
        delegateePathToImage,
        delegatorPathToImage,
        approvalFlowStepFriendlyName,
        delegateeFriendlyName,
        delegatorFriendlyName,
        activeOnlyIfOutOfOffice,
    } = data

    const [isCardSelected, setIsCardSelected] = useState(null)
    useEffect(() => {
        setIsCardSelected(checked || selectAll)
    }, [selectAll, checked])
    const { t } = useTranslation()

    const pathToImage = showDelegator
        ? delegatorPathToImage
        : delegateePathToImage

    const friendlyName = showDelegator
        ? delegatorFriendlyName
        : delegateeFriendlyName

    function getDetailsTable(dataMap) {
        return (
            <StyledTable>
                <thead>
                    <tr>
                        {Object.keys(dataMap).map((k, index) => {
                            return (
                                <th
                                    key={'dm-h-k' + index}
                                    style={{ textAlign: 'left' }}
                                    scope="col"
                                >
                                    <Typography
                                        style={{
                                            textTransform: 'uppercase',
                                            fontSize: '8px',
                                        }}
                                        variant="subtitle1"
                                    >
                                        {k}
                                    </Typography>
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {Object.keys(dataMap).map((k, index) => {
                            return (
                                <td
                                    key={'dm-v-k' + index}
                                    style={{ textAlign: 'left' }}
                                    className={dataMap[k].class}
                                >
                                    <Typography
                                        className={dataMap[k].class}
                                        variant="body2"
                                    >
                                        {dataMap[k].value}
                                    </Typography>
                                </td>
                            )
                        })}
                    </tr>
                </tbody>
            </StyledTable>
        )
    }

    const delegationCard = (
        <>
            <Box
                style={{
                    display: 'flex',
                    paddingTop: '16px',
                    marginBottom: '8px',
                }}
            >
                {!hideActions && (
                    <>
                        {rowSelection && (
                            <Checkbox
                                checked={isCardSelected}
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                data-ai-explain={`Select delegation: ${friendlyName}`}
                                onChange={() => {
                                    if (isNilOrEmpty(isCardSelected)) {
                                        setIsCardSelected(true)
                                    } else {
                                        setIsCardSelected((s) => !s)
                                        if (selectAll) {
                                            setSelectAll(false)
                                        }
                                    }
                                    handleCardChecked(data)
                                }}
                            />
                        )}
                    </>
                )}

                <div style={{ margin: '0px 14px 0px 10px' }}>
                    <StyledAvatar
                        src={
                            pathToImage &&
                            MIDPathToImage(appConfig.BASE_EID_URL, pathToImage)
                        }
                        aria-label={friendlyName}
                    />
                </div>
                <StyledFieldBlock>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('PersonName')}
                        </Typography>
                        <Typography variant="body2">
                            <StyledLink
                                component="span"
                                data-ai-explain={`Open delegation person: ${friendlyName}`}
                            >
                                <TextFormatter
                                    value={friendlyName}
                                    maxTextWidth={150}
                                />
                            </StyledLink>
                        </Typography>
                    </Box>
                </StyledFieldBlock>
                <StyledFieldBlock style={{ marginLeft: '15px' }}>
                    <Box>
                        <Typography
                            style={{
                                textTransform: 'uppercase',
                                fontSize: '8px',
                            }}
                            variant="subtitle1"
                        >
                            {t('ApprovalStep')}
                        </Typography>
                        <StyledTypography variant="body2">
                            <TextFormatter
                                value={approvalFlowStepFriendlyName}
                                maxTextWidth={250}
                            />
                        </StyledTypography>
                    </Box>
                </StyledFieldBlock>
            </Box>
            <Divider style={{ backgroundColor: '#efeff1' }} />
            <StyledCardContent>
                {getDetailsTable({
                    [t('StartDate')]: {
                        value: (
                            <DynamicComponents.CellDateTime
                                data={data}
                                attribute={{ name: 'startDateUtc' }}
                                multiline={true}
                            />
                        ),
                        class: '',
                    },
                    [t('EndDate')]: {
                        value: (
                            <DynamicComponents.CellDateTime
                                data={data}
                                attribute={{ name: 'endDateUtc' }}
                                multiline={true}
                            />
                        ),
                        class: '',
                    },
                    [t('OnlyWhenOutOfOffice')]: {
                        value: (
                            <TextFormatter
                                value={activeOnlyIfOutOfOffice}
                                maxTextWidth={110}
                            />
                        ),
                        class: '',
                    },
                })}
            </StyledCardContent>
            {!hideActions && (
                <StyledCardActions disableSpacing>
                    <div
                        style={{
                            textAlign: 'center',
                            width: '100%',
                            padding: '5px 5px',
                            display: 'flex',
                            justifyContent: 'start',
                            gap: '16px',
                        }}
                        data-ai-explain="Delegation card actions: edit or delete this delegation"
                    >
                        <DynamicComponents.EditDelegateButton
                            data={data}
                            attribute={attributes.find(
                                (x) => x.name === 'editButton',
                            )}
                        />

                        <DynamicComponents.DeleteDelegateButton
                            data={data}
                            attribute={attributes.find(
                                (x) => x.name === 'deleteButton',
                            )}
                        />
                    </div>
                </StyledCardActions>
            )}
        </>
    )

    return (
        <MyCard
            data-ai-explain={`Delegation card: ${friendlyName}`}
            data-ai-explain-section="listing"
        >
            {delegationCard}
        </MyCard>
    )
}

DelegationCard.propTypes = {
    data: PropTypes.object.isRequired,
}

export default withRouter(DelegationCard)
